import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import CustomButton from '../../../components/common/CustomButton/CustomButton';

const OTP_LENGTH = 4;

const validationSchema = Yup.object(
    Array.from({ length: OTP_LENGTH }).reduce((acc, _, idx) => {
        acc[`otp${idx}`] = Yup.string()
            .required('Required')
            .matches(/^[0-9]$/, 'Must be a digit');
        return acc;
    }, {})
);

const initialValues = Array.from({ length: OTP_LENGTH }).reduce((acc, _, idx) => {
    acc[`otp${idx}`] = '';
    return acc;
}, {});

const OtpVerificationForm = ({ mobileNumber, onSubmit, onResend, showSubText, showButton = true, formikRef }) => {
    const [timer, setTimer] = useState(30);
    const inputRefs = useRef([]);

    useEffect(() => {
        const countdown = setInterval(() => {
            setTimer((prev) => {
                if (prev === 1) clearInterval(countdown);
                return prev > 0 ? prev - 1 : 0;
            });
        }, 1000);
        return () => clearInterval(countdown);
    }, []);

    const handleResend = () => {
        if (timer === 0) {
            setTimer(30);
            onResend?.(() => {
                formikRef.current?.resetForm();
                inputRefs.current[0]?.focus();
            });
        }
    };

    const handleInputChange = (e, index, handleChange) => {
        const value = e.target.value.replace(/\D/g, '');
        e.target.value = value;
        handleChange(e);
        if (value && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !e.target.value && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    return (
        <div className="max-w-sm mx-auto">
            {showSubText ? (
                <p className="text-gray-700 text-xs my-5 text-start">
                    We have sent the verification code on your device
                </p>
            ) : null}
            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, {setSubmitting}) => {
                    const otp = Object.values(values).join('');
                    onSubmit?.(otp, setSubmitting);
                }}
            >
                {({ handleChange, isSubmitting, values }) => (
                    <Form className="space-y-6">
                        <div className="flex justify-between gap-1">
                            {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                                <input
                                    key={index}
                                    name={`otp${index}`}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength="1"
                                    className="w-12 h-12 text-center border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={values[`otp${index}`]}
                                    onChange={(e) => handleInputChange(e, index, handleChange)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    ref={(el) => (inputRefs.current[index] = el)}
                                />
                            ))}
                        </div>

                        <div className="text-center text-sm mt-4">
                            {timer > 0 ? (
                                <p className="text-gray-500">
                                    <span className="text-blue-500">Resend in: </span>
                                    {timer}s
                                </p>
                            ) : (
                                <CustomButton
                                    type="button"
                                    variant="text"
                                    label="Resend OTP"
                                    onClick={handleResend}
                                    className="text-blue-600 font-medium"
                                    fullWidth
                                />
                            )}
                        </div>

                        {showButton ? (
                            <CustomButton
                                type="submit"
                                label="Continue"
                                variant="contained"
                                isLoading={isSubmitting}
                                disabled={isSubmitting}
                                fullWidth
                            />
                        ) : null}
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default OtpVerificationForm;
